import axios from 'axios';
import { API_DOMAIN } from '../../../components/constants';
import { peopleOnly } from '../../access/models/accountKinds';
import { readStaffDirectory } from '../../../components/qv4/models/shared/staffDirectoryRead';
import { apiGet, apiPost, apiPatch, apiDelete, authHeaders } from '../../../components/qv4/models/shared/apiClient';
import { registerSessionReset, sessionTicket, sessionIsCurrent } from '../../../utils/sessionLifecycle';
const base = '/sourcing-team-v1';
const refusedInboxSessions = new Map(), inboxRequests = new Map();
registerSessionReset(() => { refusedInboxSessions.clear(); inboxRequests.clear(); });
export const inboxForbidden = error => error?.response?.status === 403 || error?.status === 403 || codeOf(error) === 'TEAM_MEMBERSHIP_REQUIRED';
const unwrap = response => {
  if (!response.data?.success) throw Object.assign(new Error(response.data?.message || 'Sourcing team request failed.'), { code: response.data?.code, data: response.data?.data });
  return response.data.data;
};
export const members = () => apiGet(`${base}/members`).then(unwrap);
export const addMember = body => apiPost(`${base}/members`, body).then(unwrap);
export const editMember = (id, body) => apiPatch(`${base}/members/${id}`, body).then(unwrap);
export const deleteMember = id => apiDelete(`${base}/members/${id}`).then(unwrap);
export function inbox(status = 'open', page = 1, limit = 30) {
  const session = sessionTicket();
  if (refusedInboxSessions.has(session)) return Promise.reject(refusedInboxSessions.get(session));
  const path = `${base}/inbox?${new URLSearchParams({ status, page, limit })}`;
  const key = JSON.stringify([session, path]);
  if (inboxRequests.has(key)) return inboxRequests.get(key);
  const pending = apiGet(path).then(unwrap).catch(error => {
    if (sessionIsCurrent(session) && inboxForbidden(error)) refusedInboxSessions.set(session, error);
    throw error;
  }).finally(() => { if (inboxRequests.get(key) === pending) inboxRequests.delete(key); });
  inboxRequests.set(key, pending);
  return pending;
}
export const enquiry = query => apiGet(`${base}/inbox/${query}`).then(unwrap);
export const answer = (query, body, key) => apiPost(`${base}/inbox/${query}/answers`, body, { headers: { 'Idempotency-Key': key } }).then(unwrap);
export const users = () => readStaffDirectory().then(unwrap).then(rows => {
  rows = peopleOnly(rows);
  // Limited directory projections omit lifecycle data; do not guess eligibility.
  if (rows.some(row => Number(row.approved) === 1 && !Object.prototype.hasOwnProperty.call(row, 'deactivated_at'))) throw new Error('The user directory does not include active-status data for this account. An administrator with HRM directory access is needed.');
  return rows.filter(row => Number(row.approved) === 1 && !row.deactivated_at).map(row => ({ id: row.id, name: [row.name, row.surname].filter(Boolean).join(' ') || row.username }));
});
export async function photo(url) {
  // Never send SRM credentials to an arbitrary URL from malformed API data.
  if (!/^\/sourcing-team-v1\/inbox\/[1-9]\d*\/photos\/(0|[1-9]\d*)$/.test(url || '')) throw new Error('Photo unavailable.');
  const response = await axios.get(`${API_DOMAIN}${url}`, { ...authHeaders(), responseType: 'blob' });
  if (!response.data?.type?.startsWith('image/')) throw new Error('Photo unavailable.');
  return response.data;
}
export const codeOf = error => error?.response?.data?.code || error?.code;
export const detailsOf = error => error?.response?.data?.data || error?.data;
export const messageOf = error => codeOf(error) === 'SOURCING_TEAM_UNAVAILABLE' ? 'Not available yet' : error?.response?.data?.message || error?.message || 'Request failed.';
export const inactiveReason = member => [!member.approved && 'User not approved', !member.active && 'User not active', Number(member.supplier_status) !== 1 && 'Supplier disabled', !member.enabled && 'Switched off'].filter(Boolean).join(' · ');
export const deliveryLabel = value => ({ app: 'App', whatsapp: 'WhatsApp', both: 'Both' }[value] || value);
export function sourcingNotice(row) {
  const query = Number(row?.data?.query_pk ?? row?.entity_id);
  return row?.type === 'sourcing_enquiry' && Number.isSafeInteger(query) && query > 0 && row.id != null
    ? { key: `sourcing:${row.id}`, title: row.title || 'New enquiry', body: row.body, route: `/enquiries-for-me?query=${query}` } : null;
}
export const markRead = id => apiPost(`/notifications/${id}/read`);
